import { generateError } from '@dx-ui/utilities-generate-error';
import { useMutation } from '@tanstack/react-query';
import { logInfo } from '@dx-ui/framework-logger';

export type ResendResponse = {
  fault?: number;
  success: boolean;
};

const resendEndpoint = '/en/auth2/api/guest/totp/generate';

/**
 * Mutation hook for generating 2FA OTP from dx-auth-api using a tempLogin webGuestToken.
 *
 * Note: Consider using `useCreateGuest2FaTotpMutation` instead when current webGuestToken is a user token.
 */
export const useAuthApi2FaResendMutation = () => {
  return useMutation<ResendResponse, Error>({
    mutationKey: [resendEndpoint],
    mutationFn: async () => {
      const options: RequestInit = {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      };
      const response = await fetch(resendEndpoint, options);
      const error = generateError(
        `Generating OTP failed: ${response.status} ${response.statusText}`
      );

      if (response.status === 401) {
        try {
          const data = (await response.json()) as ResendResponse;
          if (data?.fault === 994) {
            logInfo('LOGIN_FORM_OTP', 'OTP resend exceeded');
            return data;
          } else {
            throw error;
          }
        } catch {
          throw error;
        }
      }

      if (!response.ok) {
        throw error;
      } else {
        return { success: true };
      }
    },
    retry: false,
  });
};
